import React, { createContext, useContext, useState, useEffect } from 'react';
import { getAuth, GoogleAuthProvider, linkWithPopup, signInAnonymously, signInWithPopup, signOut, onAuthStateChanged } from 'firebase/auth';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const auth = getAuth();

  const ensureAnonymousUser = async () => {
    if (auth.currentUser) return auth.currentUser;
    try {
      setError(null);
      const result = await signInAnonymously(auth);
      setUser(result.user);
      return result.user;
    } catch (error) {
      setUser(null);
      setError(error.message);
      throw error;
    }
  };

  useEffect(() => {
    let cancelled = false;
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setError(null);
      if (user) {
        setUser(user);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const result = await signInAnonymously(auth);
        if (cancelled) return;
        setUser(result.user);
        setLoading(false);
      } catch (error) {
        if (cancelled) return;
        setUser(null);
        setError(error.message);
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [auth]);

  const loginWithGoogle = async () => {
    try {
      setError(null);
      const provider = new GoogleAuthProvider();
      const result = auth.currentUser?.isAnonymous
        ? await linkWithPopup(auth.currentUser, provider)
        : await signInWithPopup(auth, provider);
      await result.user.getIdToken(true);
      setUser(result.user);
      return result.user;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const logout = async () => {
    try {
      setError(null);
      await signOut(auth);
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const value = {
    user,
    loading,
    error,
    ensureAnonymousUser,
    loginWithGoogle,
    logout,
    isAnonymous: Boolean(user?.isAnonymous),
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
